import { Button } from '@/components/Button';
import { Form } from '@/components/Form';
import { Flex } from '@/components/Grid';
import { Heading } from '@/components/Heading';
import { Link } from '@/components/Link';
import { Text } from '@/components/Text';
import { ImageUpload } from '@/components/Upload';
import { useModal } from '@/hooks/useModal';
import { type AdType } from '@/resource/ad';
import type { SetState, ImageUploadType } from '@/util/types';
import { useRef } from 'react';
import { CancelLink } from './CancelLink';

type Props = {
  adType: AdType;
  uploads: ImageUploadType[];
  setUploads: SetState<ImageUploadType[]>;
  onBack: () => void;
  onNext: () => void;
  max?: number;
};

export function SelectImageForm({
  adType,
  uploads,
  setUploads,
  onBack,
  onNext,
  max = 6,
}: Props) {
  const modal = useModal();
  const ref = useRef<HTMLParagraphElement>(null);
  const refocus = () => ref.current?.focus();
  const label = adType === 'offer' ? 'aanbod' : 'vraag';
  const modalTitle = 'Foto bijsnijden';

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
    >
      <Flex dir="column" gap={4}>
        <Heading visualLevel={3} align="center" balance>
          Kies een foto
        </Heading>

        <Flex dir="column" gap={2}>
          {uploads.map(({ original, crop }, index) => (
            <ImageUpload
              key={`${index}_${original.size}_${original.type}`}
              image={crop}
              onRemove={() => {
                setUploads((uploads) => uploads.filter((_, i) => i !== index));
              }}
              onChange={(file) => {
                modal.open(
                  {
                    type: 'image_cropper',
                    props: {
                      title: modalTitle,
                      aspectRatio: 16 / 9,
                      file,
                      allowRotation: false,
                      onCancel() {
                        modal.close();
                      },
                      onSave(upload) {
                        setUploads((curr) => [
                          ...curr.slice(0, index),
                          ...[upload],
                          ...curr.slice(index + 1),
                        ]);
                        modal.close();
                      },
                    },
                  },
                  refocus
                );
              }}
            />
          ))}

          {uploads.length < max && (
            <>
              <ImageUpload
                label={
                  uploads.length > 0
                    ? 'Upload nog een foto'
                    : 'Upload een eigen foto'
                }
                onChange={(file) => {
                  modal.open(
                    {
                      type: 'image_cropper',
                      props: {
                        title: modalTitle,
                        aspectRatio: 16 / 9,
                        file,
                        allowRotation: false,
                        onCancel() {
                          modal.close();
                        },
                        onSave(upload) {
                          setUploads((curr) => curr.concat([upload]));
                          modal.close();
                        },
                      },
                    },
                    refocus
                  );
                }}
              />
              <Text ref={ref} tabIndex={0}>
                {uploads.length > 0 ? (
                  <>
                    Eén foto is genoeg, maar als je wilt kun je nog meer foto's
                    toevoegen. De eerste foto wordt gebruikt in het overzicht.
                  </>
                ) : (
                  <>
                    Voor het beste resultaat kies je een liggende foto in de
                    verhouding 16 bij 9. Als je geen foto upload dan komt jouw
                    profielfoto bij je {label} te staan.{' '}
                    <Link
                      to={{ type: 'profile_info' }}
                      underline
                      target="_blank"
                    >
                      Pas je profielfoto aan
                    </Link>
                    .
                  </>
                )}
              </Text>
            </>
          )}
        </Flex>

        <Flex justify="space-between">
          <Button
            theme="secondary"
            onClick={onBack}
            icon="chevron_left_narrow"
            iconSize={2}
          >
            Terug
          </Button>
          <Button
            type="submit"
            icon="chevron_right_narrow"
            iconPosition="right"
            iconSize={2}
          >
            Naar voorbeeld
          </Button>
        </Flex>

        <CancelLink confirm adType={adType} />
      </Flex>
    </Form>
  );
}
